/**
 * Content Type Detection
 *
 * Detects MIME types from file signatures (magic bytes) and content analysis.
 */

import { CLIPBOARD_MIME_TYPES, type ClipboardMimeType } from './types.js';

// ============================================================================
// Magic Byte Signatures
// ============================================================================

interface MagicSignature {
  bytes: number[];
  offset?: number;
  mimeType: ClipboardMimeType;
}

/**
 * Known file signatures (magic bytes)
 */
const MAGIC_SIGNATURES: MagicSignature[] = [
  // Images
  { bytes: [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A], mimeType: CLIPBOARD_MIME_TYPES.IMAGE_PNG },
  { bytes: [0xFF, 0xD8, 0xFF], mimeType: CLIPBOARD_MIME_TYPES.IMAGE_JPEG },
  { bytes: [0x47, 0x49, 0x46, 0x38, 0x37, 0x61], mimeType: CLIPBOARD_MIME_TYPES.IMAGE_GIF }, // GIF87a
  { bytes: [0x47, 0x49, 0x46, 0x38, 0x39, 0x61], mimeType: CLIPBOARD_MIME_TYPES.IMAGE_GIF }, // GIF89a
  { bytes: [0x52, 0x49, 0x46, 0x46], mimeType: CLIPBOARD_MIME_TYPES.IMAGE_WEBP }, // RIFF (WebP container)

  // Video
  { bytes: [0x1A, 0x45, 0xDF, 0xA3], mimeType: CLIPBOARD_MIME_TYPES.VIDEO_WEBM }, // WebM/MKV
  { bytes: [0x00, 0x00, 0x00], mimeType: CLIPBOARD_MIME_TYPES.VIDEO_MP4 }, // ftyp box (MP4)

  // Audio
  { bytes: [0x49, 0x44, 0x33], mimeType: CLIPBOARD_MIME_TYPES.AUDIO_MP3 }, // ID3
  { bytes: [0xFF, 0xFB], mimeType: CLIPBOARD_MIME_TYPES.AUDIO_MP3 }, // MP3 frame

  // Documents
  { bytes: [0x25, 0x50, 0x44, 0x46], mimeType: CLIPBOARD_MIME_TYPES.PDF }, // %PDF

  // AOS-specific (JSON-based, detected by content)
];

/**
 * Code file extensions to MIME types
 */
const CODE_EXTENSIONS: Record<string, string> = {
  // JavaScript/TypeScript
  js: 'text/x-javascript',
  mjs: 'text/x-javascript',
  cjs: 'text/x-javascript',
  ts: 'text/x-typescript',
  tsx: 'text/x-tsx',
  jsx: 'text/x-jsx',

  // Python
  py: 'text/x-python',
  pyw: 'text/x-python',
  pyi: 'text/x-python',

  // Rust
  rs: 'text/x-rust',

  // Go
  go: 'text/x-go',

  // C/C++
  c: 'text/x-c',
  h: 'text/x-c',
  cpp: 'text/x-cpp',
  hpp: 'text/x-cpp',
  cc: 'text/x-cpp',
  cxx: 'text/x-cpp',

  // Java/Kotlin
  java: 'text/x-java',
  kt: 'text/x-kotlin',
  kts: 'text/x-kotlin',

  // Ruby
  rb: 'text/x-ruby',

  // PHP
  php: 'text/x-php',

  // Shell
  sh: 'text/x-shellscript',
  bash: 'text/x-shellscript',
  zsh: 'text/x-shellscript',

  // Config
  json: 'application/json',
  yaml: 'text/x-yaml',
  yml: 'text/x-yaml',
  toml: 'text/x-toml',
  ini: 'text/x-ini',

  // Web
  html: 'text/html',
  htm: 'text/html',
  css: 'text/css',
  scss: 'text/x-scss',
  sass: 'text/x-sass',
  less: 'text/x-less',

  // Data
  xml: 'text/xml',
  csv: 'text/csv',
  sql: 'text/x-sql',

  // Markdown
  md: 'text/markdown',
  markdown: 'text/markdown',

  // Plain text
  txt: 'text/plain',
  log: 'text/plain',
};

// ============================================================================
// Detection Functions
// ============================================================================

/**
 * Detect MIME type from binary data using magic bytes
 */
export function detectFromBytes(data: Uint8Array): ClipboardMimeType | null {
  for (const sig of MAGIC_SIGNATURES) {
    const offset = sig.offset ?? 0;
    if (data.length < offset + sig.bytes.length) continue;

    let match = true;
    for (let i = 0; i < sig.bytes.length; i++) {
      if (data[offset + i] !== sig.bytes[i]) {
        match = false;
        break;
      }
    }
    if (match) return sig.mimeType;
  }

  // Check for WebP specifically (RIFF....WEBP)
  if (data.length >= 12) {
    const riff = data[0] === 0x52 && data[1] === 0x49 && data[2] === 0x46 && data[3] === 0x46;
    const webp = data[8] === 0x57 && data[9] === 0x45 && data[10] === 0x42 && data[11] === 0x50;
    if (riff && webp) return CLIPBOARD_MIME_TYPES.IMAGE_WEBP;
  }

  // Check for MP4 (ftyp at offset 4)
  if (data.length >= 8) {
    const ftyp = data[4] === 0x66 && data[5] === 0x74 && data[6] === 0x79 && data[7] === 0x70;
    if (ftyp) return CLIPBOARD_MIME_TYPES.VIDEO_MP4;
  }

  return null;
}

/**
 * Detect MIME type from filename extension
 */
export function detectFromFilename(filename: string): ClipboardMimeType | null {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (!ext) return null;

  // Check code extensions
  if (CODE_EXTENSIONS[ext]) {
    return CODE_EXTENSIONS[ext];
  }

  // Common non-code extensions
  switch (ext) {
    case 'png': return CLIPBOARD_MIME_TYPES.IMAGE_PNG;
    case 'jpg':
    case 'jpeg': return CLIPBOARD_MIME_TYPES.IMAGE_JPEG;
    case 'gif': return CLIPBOARD_MIME_TYPES.IMAGE_GIF;
    case 'webp': return CLIPBOARD_MIME_TYPES.IMAGE_WEBP;
    case 'svg': return CLIPBOARD_MIME_TYPES.IMAGE_SVG;
    case 'webm': return CLIPBOARD_MIME_TYPES.VIDEO_WEBM;
    case 'mp4':
    case 'm4v': return CLIPBOARD_MIME_TYPES.VIDEO_MP4;
    case 'mp3': return CLIPBOARD_MIME_TYPES.AUDIO_MP3;
    case 'pdf': return CLIPBOARD_MIME_TYPES.PDF;
    default: return null;
  }
}

/**
 * Detect if text content is likely code
 */
export function detectCodeLanguage(text: string): string | null {
  // Check for shebang
  if (text.startsWith('#!')) {
    const line = text.split('\n')[0];
    if (line.includes('python')) return 'text/x-python';
    if (line.includes('node') || line.includes('deno') || line.includes('bun')) return 'text/x-javascript';
    if (line.includes('bash') || line.includes('sh')) return 'text/x-shellscript';
    if (line.includes('ruby')) return 'text/x-ruby';
    if (line.includes('php')) return 'text/x-php';
    if (line.includes('perl')) return 'text/x-perl';
  }

  // Check for common patterns
  const patterns: [RegExp, string][] = [
    // JavaScript/TypeScript
    [/^(import|export)\s+(default\s+)?(class|function|const|let|var|type|interface)\b/m, 'text/x-javascript'],
    [/^(const|let|var)\s+\w+\s*=\s*(require|async|function|\(|{|\[)/m, 'text/x-javascript'],
    [/^interface\s+\w+\s*{/m, 'text/x-typescript'],
    [/^type\s+\w+\s*=\s*/m, 'text/x-typescript'],

    // Python
    [/^(def|class|import|from|async def)\s+\w+/m, 'text/x-python'],
    [/^if\s+__name__\s*==\s*['"]__main__['"]/m, 'text/x-python'],

    // Rust
    [/^(fn|struct|enum|impl|trait|mod|use|pub)\s+/m, 'text/x-rust'],
    [/^#\[(derive|cfg|test|allow|deny)\(/m, 'text/x-rust'],

    // Go
    [/^package\s+\w+$/m, 'text/x-go'],
    [/^func\s+(\(\w+\s+\*?\w+\)\s+)?\w+\(/m, 'text/x-go'],

    // C/C++
    [/^#include\s*[<"]/m, 'text/x-c'],
    [/^(int|void|char|double|float)\s+main\s*\(/m, 'text/x-c'],
    [/^(class|namespace|template)\s+\w+/m, 'text/x-cpp'],

    // Java
    [/^(public|private|protected)?\s*(static)?\s*(class|interface|enum)\s+\w+/m, 'text/x-java'],
    [/^package\s+[\w.]+;$/m, 'text/x-java'],

    // HTML
    [/^<!DOCTYPE\s+html>/i, 'text/html'],
    [/^<html[\s>]/i, 'text/html'],

    // CSS
    [/^[.#]?\w+\s*{[\s\S]*?}$/m, 'text/css'],
    [/^@(import|media|keyframes|font-face)\s+/m, 'text/css'],

    // JSON
    [/^\s*\{[\s\S]*"[\w-]+"\s*:/, 'application/json'],
    [/^\s*\[[\s\S]*\{/, 'application/json'],

    // YAML
    [/^---\s*$/m, 'text/x-yaml'],
    [/^\w+:\s*$/m, 'text/x-yaml'],

    // Markdown
    [/^#\s+\w+/m, 'text/markdown'],
    [/^\*\*[\w\s]+\*\*/m, 'text/markdown'],
    [/^-\s+\[[ x]\]/m, 'text/markdown'],

    // Shell
    [/^(export|alias|source|echo)\s+/m, 'text/x-shellscript'],
    [/^\$\s+\w+/m, 'text/x-shellscript'],
  ];

  for (const [pattern, mimeType] of patterns) {
    if (pattern.test(text)) {
      return mimeType;
    }
  }

  return null;
}

/**
 * Detect if content is AOS-specific type by parsing JSON
 */
export function detectAOSType(text: string): ClipboardMimeType | null {
  try {
    const parsed = JSON.parse(text);
    if (typeof parsed !== 'object' || parsed === null) return null;

    // Check for AOS-specific type field
    switch (parsed.type) {
      case 'aos_boot':
      case 'aos.boot':
        return CLIPBOARD_MIME_TYPES.BOOT_IMAGE;
      case 'aos_package':
      case 'aos.package':
      case 'aos.module':
        return CLIPBOARD_MIME_TYPES.PACKAGE;
      case 'aos_invite':
      case 'aos.invite':
        return CLIPBOARD_MIME_TYPES.INVITE;
      case 'aos_grant':
      case 'aos.grant':
        return CLIPBOARD_MIME_TYPES.GRANT;
      default:
        return null;
    }
  } catch {
    return null;
  }
}

/**
 * Comprehensive MIME type detection
 *
 * Priority:
 * 1. Explicit type provided
 * 2. Magic bytes detection
 * 3. Filename extension
 * 4. AOS type detection (for JSON)
 * 5. Code language detection
 * 6. Default to octet-stream or plain text
 */
export function detectMimeType(
  data: Uint8Array | string,
  options?: {
    filename?: string;
    explicitType?: ClipboardMimeType;
  }
): ClipboardMimeType {
  // 1. Explicit type
  if (options?.explicitType) {
    return options.explicitType;
  }

  // Convert to bytes if string
  const bytes = typeof data === 'string'
    ? new TextEncoder().encode(data)
    : data;

  // 2. Magic bytes
  const magicType = detectFromBytes(bytes);
  if (magicType) return magicType;

  // 3. Filename extension
  if (options?.filename) {
    const extType = detectFromFilename(options.filename);
    if (extType) return extType;
  }

  // For text content, try text-based detection
  let text: string;
  try {
    text = typeof data === 'string' ? data : new TextDecoder().decode(bytes);
  } catch {
    // Not valid UTF-8, treat as binary
    return CLIPBOARD_MIME_TYPES.OCTET_STREAM;
  }

  // 4. AOS type detection
  const aosType = detectAOSType(text);
  if (aosType) return aosType;

  // 5. Code language detection
  const codeType = detectCodeLanguage(text);
  if (codeType) return codeType;

  // 6. Default
  // Check if content looks like text
  const isText = /^[\x20-\x7E\t\n\r]+$/.test(text.slice(0, 1000));
  return isText ? CLIPBOARD_MIME_TYPES.TEXT_PLAIN : CLIPBOARD_MIME_TYPES.OCTET_STREAM;
}

/**
 * Get human-readable content type label
 */
export function getContentTypeLabel(mimeType: ClipboardMimeType): string {
  const labels: Record<string, string> = {
    [CLIPBOARD_MIME_TYPES.BOOT_IMAGE]: 'AOS Boot Image',
    [CLIPBOARD_MIME_TYPES.PACKAGE]: 'AOS Package',
    [CLIPBOARD_MIME_TYPES.INVITE]: 'AOS Invite',
    [CLIPBOARD_MIME_TYPES.GRANT]: 'Export Grant',
    [CLIPBOARD_MIME_TYPES.TEXT_PLAIN]: 'Plain Text',
    [CLIPBOARD_MIME_TYPES.TEXT_HTML]: 'HTML',
    [CLIPBOARD_MIME_TYPES.TEXT_MARKDOWN]: 'Markdown',
    [CLIPBOARD_MIME_TYPES.IMAGE_PNG]: 'PNG Image',
    [CLIPBOARD_MIME_TYPES.IMAGE_JPEG]: 'JPEG Image',
    [CLIPBOARD_MIME_TYPES.IMAGE_GIF]: 'GIF Image',
    [CLIPBOARD_MIME_TYPES.IMAGE_WEBP]: 'WebP Image',
    [CLIPBOARD_MIME_TYPES.IMAGE_SVG]: 'SVG Image',
    [CLIPBOARD_MIME_TYPES.VIDEO_WEBM]: 'WebM Video',
    [CLIPBOARD_MIME_TYPES.VIDEO_MP4]: 'MP4 Video',
    [CLIPBOARD_MIME_TYPES.AUDIO_WEBM]: 'WebM Audio',
    [CLIPBOARD_MIME_TYPES.AUDIO_MP3]: 'MP3 Audio',
    [CLIPBOARD_MIME_TYPES.PDF]: 'PDF Document',
    [CLIPBOARD_MIME_TYPES.JSON]: 'JSON',
    [CLIPBOARD_MIME_TYPES.OCTET_STREAM]: 'Binary Data',
  };

  if (labels[mimeType]) return labels[mimeType];

  // Handle code types
  if (mimeType.startsWith('text/x-')) {
    const lang = mimeType.slice(7);
    return lang.charAt(0).toUpperCase() + lang.slice(1) + ' Code';
  }

  // Handle wildcards
  if (mimeType.startsWith('image/')) return 'Image';
  if (mimeType.startsWith('video/')) return 'Video';
  if (mimeType.startsWith('audio/')) return 'Audio';
  if (mimeType.startsWith('text/')) return 'Text';

  return mimeType;
}

/**
 * Check if content type is an AOS system type (boot, package, invite, grant)
 */
export function isAOSSystemType(mimeType: ClipboardMimeType): boolean {
  return (
    mimeType === CLIPBOARD_MIME_TYPES.BOOT_IMAGE ||
    mimeType === CLIPBOARD_MIME_TYPES.PACKAGE ||
    mimeType === CLIPBOARD_MIME_TYPES.INVITE ||
    mimeType === CLIPBOARD_MIME_TYPES.GRANT
  );
}

/**
 * Check if content type is media (image, video, audio)
 */
export function isMediaType(mimeType: ClipboardMimeType): boolean {
  return (
    mimeType.startsWith('image/') ||
    mimeType.startsWith('video/') ||
    mimeType.startsWith('audio/')
  );
}

/**
 * Check if content type is code
 */
export function isCodeType(mimeType: ClipboardMimeType): boolean {
  return mimeType.startsWith('text/x-');
}
